import type { RequestInterface } from '~/common/types';
import { RoleID } from '~/roles/types';
import { CentralUser, NewUserRequest, User } from '~/users/types';
import type { GroupID } from '~/groups/types';

export class UsersRequest implements RequestInterface {
  method = 'get';
  path = '/api/v1/users';
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    params,
  }: {
    token: string;
    params?: Record<string, any>;
  }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class GetUserRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    params,
  }: {
    token: string;
    userId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}`;

    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class AssignRolesRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: Array<RoleID>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    roles,
    params,
  }: {
    token: string;
    userId: string;
    roles: Array<RoleID>;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/assign/roles`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = roles;
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateUserGroupsRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: GroupID[];
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    groupIds,
    params,
  }: {
    token: string;
    userId: string;
    groupIds: GroupID[];
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/groups`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = groupIds;
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateFSSAdminCompanyRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    companyId,
    params,
  }: {
    token: string;
    companyId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/fss/company/${companyId}`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateUserProfileRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    user,
    params,
  }: {
    token: string;
    userId: string;
    user: User;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/profile`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = user;
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class UpdateCentralProfileRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  data: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    central,
    params,
  }: {
    token: string;
    userId: string;
    central: CentralUser;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/central`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = central;
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class DisableUserRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    params,
  }: {
    token: string;
    userId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/status/disabled`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class ActivateUserRequest implements RequestInterface {
  method = 'patch';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    params,
  }: {
    token: string;
    userId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/status/active`;
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class GetUserTicketsRequest implements RequestInterface {
  method = 'get';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    params,
  }: {
    token: string;
    userId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/tickets/${userId}`;

    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}

export class CreateUserRequest implements RequestInterface {
  method = 'post';
  path = '/api/v1/users';
  headers: Record<string, any>;
  data: Record<string, any>;

  constructor({ token, user }: { token: string; user: NewUserRequest }) {
    this.headers = {
      authorization: `Bearer ${token}`,
    };
    this.data = {
      ...user,
    };
  }
}

export class SendWelcomeEmailRequest implements RequestInterface {
  method = 'post';
  path: string;
  headers: Record<string, any>;
  params?: Record<string, any>;

  constructor({
    token,
    userId,
    params,
  }: {
    token: string;
    userId: string;
    params?: Record<string, any>;
  }) {
    this.path = `/api/v1/users/${userId}/send-welcome-email`;

    this.headers = {
      authorization: `Bearer ${token}`,
    };
    if (params) {
      this.params = {
        ...params,
      };
    }
  }
}
